package com.neurobs.ccabserver.administrator.components

import com.neurobs.ccab.{CCABServer, ReportHistoryData, SubjectSessionsRequestData}
import com.neurobs.ccabserver.administrator.contexts.{GuiContext, ServerContext, UserContext}
import com.neurobs.ccabserver.administrator.controller.{ExaminerTagList, SessionLogController}
import com.neurobs.ccabserver.subjectsessionview.SubjectSessionView
import com.neurobs.errors.Result
import com.neurobs.js.controller.AdminPanelAdapter
import com.neurobs.react.{Button, CardContentProvider, StyleUtil}
import com.neurobs.util.{Guid, PrintLogger}
import slinky.core.FunctionalComponent
import slinky.core.annotations.react
import slinky.core.facade.Hooks.{useEffect, useState}
import slinky.core.facade.ReactElement
import slinky.web.html.{div, style}
import org.scalajs.dom
import scala.scalajs.js.URIUtils

import scala.concurrent.Future
import scala.concurrent.ExecutionContext.Implicits.global

@react object ResultsSubject {
	case class Props
	(
		accountId: Long,
		accountSubjectId: Long,
		reportingId: Guid,
		dataPassword: String,
		delegate: CardContentProvider.Delegate
	)
	val component = FunctionalComponent[Props] { props =>
		val user = UserContext.accessState()
		val gui = GuiContext.accessState()
		val server = ServerContext.accessState()

		val (history, setHistory) = useState[Option[ReportHistoryData]](None)

		useEffect(() => {
			server.getAccountSubjectReportHistory(props.accountSubjectId)._2 map {
				case Left(_) =>
				case Right(history) => setHistory(Some(history))
			}
		}, Vector())

		def openSessionLogPanel: (Guid, Long) => Unit = (subjectGuid: Guid, sessionId: Long) => {
			server.getExaminerTags()._2 map {
				case Left(_) =>
				case Right(tags) =>
					props.delegate.addCard(
						card = new AdminPanelAdapter(panel = new SessionLogController(gui, new PrintLogger(printDebug = false), subjectGuid, sessionId, tags = new ExaminerTagList(tags))),
						afterThis = true
					)
			}
		}

		val request = SubjectSessionsRequestData(accountId = props.accountId, accountSubjectId = props.accountSubjectId, dataPassword = Some(props.dataPassword))
		val latestReportsBySession = history.toSeq
			.flatMap(_.reports)
			.groupBy(_.studySessionId)
			.values
			.flatMap(_.sortBy(_.generationTime).lastOption)
			.toSeq
			.sortBy(_.studySessionId.noBracketsString)

		div(
			SubjectSessionView(
				requestData = request,
				userData = user.user.get,
				openSessionLogPanel = openSessionLogPanel,
				defaultSessionId = None,
				defaultTestRunId = None
			),
			if (latestReportsBySession.nonEmpty) {
				div(style := StyleUtil.makeStyle(("marginTop", "1rem"))) {
					latestReportsBySession.map { report =>
						val encoded = URIUtils.encodeURIComponent(report.localFilename)
						div(style := StyleUtil.makeStyle(("marginBottom", "0.5rem")))(
							Button(
								caption = s"Score Report (${report.studySessionId.noBracketsString})",
								onClick = () => {
									dom.window.open(s"api/v1/account_subjects/${props.accountSubjectId}/report?file=$encoded", "_blank")
								}
							)
						)
					}
				}
			} else None
		)
	}
}

class ResultsSubjectProvider(accountId: Long, accountSubjectId: Long, reportingId: Guid, dataPassword: String) extends CardContentProvider {

	override def title: String = s"Subject ${reportingId.noBracketsString}"

	override def canClose: Boolean = true

	override def refreshFunction: Option[CCABServer => Future[Result[AnyRef]]] = None

	override def content(d: CardContentProvider.Delegate, contentData: Option[AnyRef]): ReactElement = {
		ResultsSubject(accountId = accountId, accountSubjectId = accountSubjectId, reportingId = reportingId, dataPassword = dataPassword, delegate = d)
	}
}
